import {
  PlanningEvent,
  PlanningStatus,
  PlanningSeverity,
} from "../models/planning-event.model";

/**
 * Transforme des commandes fournisseur (déjà chargées front) en PlanningEvent[].
 * Mode "parent-fetch" — zéro appel API supplémentaire.
 */
export function commandesToPlanningEvents(commandes: any[]): PlanningEvent[] {
  if (!commandes?.length) return [];

  const today = new Date();

  return commandes
    .filter((c) => c.dateLivraisonPrevue != null)
    // Exclut les commandes annulées : flag dateAnnulation (Phase 4, réversible)
    // ou ancien etat "ANNULEE". Une commande annulée n'a aucune activité à venir.
    .filter((c) => c.dateAnnulation == null && c.etat !== "ANNULEE")
    .map((c) => {
      const { status, severity } = deriveStatusFromEtat(
        c.etat,
        c.dateLivraisonPrevue,
        today,
      );

      const fournisseur = c.tiers?.denomination ?? "";
      const numero = c.numero ?? "?";

      return {
        id: `commande:${c.id}`,
        source: "commande" as const,
        kind: "delivery",
        reference: {
          label: fournisseur || numero,
          route: [`/commandes/details/id/${c.id}/commande`],
        },
        datePrevu: c.dateLivraisonPrevue
          ? new Date(c.dateLivraisonPrevue).toISOString()
          : null,
        dateReel: c.dateFermeture ? new Date(c.dateFermeture).toISOString() : null,
        status,
        severity,
        quantite: undefined,
        payload: {
          fournisseur,
          numero,
          etat: c.etat,
          etatLabel: etatToLabel(c.etat),
        },
      } satisfies PlanningEvent;
    });
}

function deriveStatusFromEtat(
  etat: string,
  dateLivraisonPrevue: Date | string | null,
  today: Date,
): { status: PlanningStatus; severity: PlanningSeverity } {
  switch (etat) {
    case "ANNULEE":
      return { status: "cancelled", severity: 0 };
    case "TERMINEE":
    case "SOLDEE":
      return { status: "completed", severity: 0 };
    case "LIVREE_PARTIELLEMENT":
      return { status: "partial", severity: 1 };
    case "EN_ATTENTE_LIVRAISON": {
      if (dateLivraisonPrevue) {
        const prevuDate = new Date(dateLivraisonPrevue);
        prevuDate.setHours(23, 59, 59, 999);
        if (prevuDate < today) {
          return { status: "late", severity: 2 };
        }
      }
      return { status: "pending", severity: 0 };
    }
    case "EN_ATTENTE_ENVOI":
    case "EN_ATTENTE_CONFIRMATION":
    case "EN_REDACTION":
    default:
      return { status: "pending", severity: 0 };
  }
}

function etatToLabel(etat: string): string {
  const labels: Record<string, string> = {
    EN_REDACTION: "Brouillon",
    EN_ATTENTE_ENVOI: "À envoyer",
    EN_ATTENTE_CONFIRMATION: "À confirmer",
    EN_ATTENTE_LIVRAISON: "Att. livraison",
    LIVREE_PARTIELLEMENT: "Partielle",
    ANNULEE: "Annulée",
    TERMINEE: "Terminée",
    SOLDEE: "Soldée",
  };
  return labels[etat] ?? etat;
}
